import { CommonModule } from '@angular/common';
import { Component, forwardRef, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-checkbox',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './checkbox.component.html',
  styleUrls: ['./checkbox.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CheckboxComponent),
      multi: true,
    },
  ],
})
export class CheckboxComponent implements ControlValueAccessor {
  @Input() id: string = '';
  @Input() labelPosition: 'top' | 'right' | 'left' | 'none' = 'right';
  @Input() label: string = '';
  @Input() error: string = '';
  @Input() customClass: string = '';
  @Input() size: 'small' | 'medium' | 'large' = 'medium';
  @Input() checked: boolean = false;
  @Input() checkboxClass: string = '';
  @Input() parentController: boolean = false;
  @Input() borderWidth: 'border' | '2' | '4' | '8' = '2';
  @Input() readonly: boolean = false;
  @Input() name: string = '';
  disabled: boolean = false;

  // Ensure each checkbox has a unique ID when not provided
  private autoId: string = 'checkbox-' + Math.random().toString(36).slice(2);

  get idFinal(): string {
    return this.id && this.id.trim().length > 0 ? this.id : this.autoId;
  }

  onChange: any = () => {};
  onTouched: any = () => {};

  writeValue(value: boolean): void {
    this.checked = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onCheckboxChange(event: Event) {
    const input = event.target as HTMLInputElement;
    if(!this.parentController){
      this.checked = input.checked;
    }
    this.onChange(this.checked);
    this.onTouched();
  }

  getCheckboxClasses(): string {
  const baseClasses = this.parentController 
    ? 'peer box-border appearance-none m-0 p-0 rounded-[8px] transition-colors pointer-events-none'
    : 'peer cursor-pointer box-border appearance-none m-0 p-0 rounded-[8px] transition-colors';

  const borderClasses = this.borderWidth === 'border'
    ? 'border border-secundary-b800 checked:border-primary-g500'
    : `border-${this.borderWidth} border-secundary-b800 checked:border-primary-g500`;

  const sizeClasses = this.size === 'small'
    ? 'w-5 h-5'
    : this.size === 'medium'
      ? 'w-6 h-6'
      : 'w-10 h-10';

  const stateClasses = this.checked
    ? 'bg-primary-g500 border-primary-g500'
    : 'bg-white border-soft-gray';

  return `${baseClasses} ${this.checkboxClass ? this.checkboxClass : ''} ${borderClasses} ${sizeClasses} ${stateClasses}`;
}
}
